import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import ContentBody from '@this/components/shared/atoms/content_body';
import { Link } from '@this/shared/ui/navigations/link';
import Tooltip from '@this/shared/tooltip/tooltip';
import { dig } from '@this/src/util';

import AlertArea from '@this/components/reserve_trip/select/alert_area/alert_area';
import { getColor } from '@this/shared/ui/theme';
import type SearchQuery from '../../../domain/search_query';
import type SearchQueryItem from '../../../domain/search_query_item';
import type { Airline } from '../../../domain/select_store';
import type User from '../../../domain/user/user';

import { useSelectContext } from './context/SelectContext';

import SelectDefaultArea from './default_area/default_area';
import SelectListArea from './list_area/list_area';
import SelectSearchArea from './search_area/search_area';
import SelectOutlineWrapper from './outline_wrapper/outline_wrapper.template';
import SelectOutlineWrapperTop from './outline_wrapper/outline_wrapper.top';

import ArrangementRequestForm from './arrangement_request_form/arrangement_request_form';

interface Props {
  showArrangementRequestForm: boolean;
  showSearchBox: boolean;
  additionalSearchErrors?: { [k in string]: string };
  serviceId: number;
  user: User | null;
  availableOptions: string[];

  handleAirline: (airline: Airline) => () => void;
  handleSelectItem: () => void;
  handleAddItem: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  handleReSearch: (query: SearchQuery) => void;
  handleToggleArrangementRequestForm: () => void;
  toggleShowSearchBox: () => void;
  handleOpenShowSearchBox: () => void;
}

const SelectSpTemplate: React.FC<Props> = ({
  showArrangementRequestForm,
  serviceId,
  user,
  availableOptions,
  showSearchBox,
  additionalSearchErrors,

  handleAirline,
  handleSelectItem,
  handleAddItem,
  handleAdditionalSearch,
  handleReSearch,
  handleToggleArrangementRequestForm,
  toggleShowSearchBox,
  handleOpenShowSearchBox
}) => {
  const { store, repository, query, selectErrors, submitting, handleSelectType, handleNextButtonClick } =
    useSelectContext();

  return (
    <div className="select-sp">
      {showArrangementRequestForm
        ? [
            <BackHeader key={0}>
              <BackHeaderA onClick={handleToggleArrangementRequestForm}>{'< 戻る'}</BackHeaderA>
            </BackHeader>,
            <Body key={1}>
              <ArrangementRequestForm
                serviceId={serviceId}
                query={query}
                store={store}
                selectErrors={selectErrors}
                handleSelectItem={handleSelectItem}
                handleAdditionalSearch={handleAdditionalSearch}
                additionalSearchErrors={additionalSearchErrors}
                handleNextButtonClick={handleNextButtonClick}
                handleOpenShowSearchBox={handleOpenShowSearchBox}
                reserveRequestMailNotificationType={
                  store.user?.organization?.reserve_request_mail_notification_type ?? 'togglable'
                }
                shortdistance={false}
              />
            </Body>
          ]
        : store.currentTab === 'none'
        ? [
            <Body key={1}>
              <SelectSearchArea
                query={query}
                user={user}
                availableOptions={availableOptions}
                trip={store.result.getReservingTrip()}
                selectErrors={selectErrors}
                submitting={submitting}
                store={store}
                handleNextButtonClick={handleNextButtonClick}
                handleReSearch={handleReSearch}
              />
              <AlertArea result={store.result} />
              <div style={{ paddingTop: '20px' }}>
                <SelectOutlineWrapperTop
                  store={store}
                  handleSelectType={handleSelectType}
                  handleAirline={handleAirline}
                  width={100}
                  serviceId={serviceId}
                />
              </div>
              <SelectOutlineWrapper
                query={query}
                store={store}
                selectErrors={selectErrors}
                packageErrors={store.packageErrors[store.result.airline]}
                handleSelectItem={handleSelectItem}
                handleAddItem={handleAddItem}
                handleAdditionalSearch={handleAdditionalSearch}
                additionalSearchErrors={additionalSearchErrors}
                handleNextButtonClick={handleNextButtonClick}
                submitting={submitting}
                handleOpenShowSearchBox={handleOpenShowSearchBox}
              />
              {dig(store, 'user', 'organization', 'arrangement_request_enable?') ? (
                <RequestText>
                  <p>
                    お探しの結果が見つからない場合は
                    <Link
                      onClick={handleToggleArrangementRequestForm}
                      style={{ borderBottom: '1px solid', cursor: 'pointer' }}
                    >
                      リクエストフォーム
                    </Link>
                    からご依頼ください
                  </p>
                  <Tooltip>
                    <p>
                      ご希望の検索結果が出てこない場合は、こちらのフォームからご依頼ください
                      <br />
                      <span data-wovn-ignore>{`（手数料: ${dig(
                        store,
                        'user',
                        'organization',
                        'arrangement_request_setting',
                        'fee'
                      )}）`}</span>
                      円
                    </p>
                  </Tooltip>
                </RequestText>
              ) : null}
              <SelectDefaultArea isOrganization show={store.currentTab === 'none'} serviceId={serviceId} sp />
            </Body>
          ]
        : [
            <BackHeader key={0}>
              <BackHeaderA onClick={() => store.handleClickTab('none')}>{'< 戻る'}</BackHeaderA>
            </BackHeader>,
            <Body key={1}>
              {store.result.items.map((item, i) => (
                <SelectListArea
                  key={item.index}
                  icon={String(i + 1)}
                  repository={repository}
                  store={store}
                  listWrapper={item.elementList}
                  type={item.elementType()}
                  subtype={item.flightIndex}
                  show={store.result.type === 'separate' && store.currentTab === item.index}
                  queryItem={query.items[i]}
                  resultItem={item}
                  query={query}
                  serviceId={serviceId}
                  showSearchBox={showSearchBox}
                  toggleShowSearchBox={toggleShowSearchBox}
                />
              ))}
              {store.result.packageItems[store.result.airline].map((item, i) => (
                <SelectListArea
                  key={item.index}
                  icon={String(i + 1)}
                  repository={repository}
                  store={store}
                  listWrapper={item.elementList}
                  type={item.elementType()}
                  subtype={item.flightIndex}
                  queryItem={query.items[i]}
                  resultItem={item}
                  query={query}
                  serviceId={serviceId}
                  show={store.result.type === 'airPackage' && store.currentTab === item.index}
                  direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                  showSearchBox={showSearchBox}
                  toggleShowSearchBox={toggleShowSearchBox}
                />
              ))}
            </Body>
          ]}
    </div>
  );
};

const BackHeader = styled.div`
  background: ${props => props.theme.backHeaderBgColor};
  padding: 10px;
`;

const BackHeaderA = styled.a`
  &,
  &:hover,
  &:focus {
    color: ${getColor('brand', 'primary')};
  }
`;

const Body = styled(ContentBody)`
  padding: 0;
`;

const RequestText = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -90px;
  padding-left: 22px;

  .tooltip__content {
    right: -60px;
  }
`;

export default SelectSpTemplate;
